import { ChangeEvent, KeyboardEvent, ReactNode, useRef, useState } from 'react';

type Fetcher = (query: string) => Promise<Item[]>;
type Callback = (item: Item) => void;
export type Item = {
  title: string;
  url?: string;
  /**
   * A thumbnail for the suggestion, and a second line under the title.
   *
   * Both optional, and both absent from the site search, which suggests titles it has
   * nothing else about. The sale page's scoped search has the whole product in hand —
   * it is searching a document the page already loaded — so its suggestions carry the
   * cover and the pricing, which is what the legacy sale search showed and what a
   * shopper actually chooses between.
   *
   * `detail` is a node rather than a string because the price in it is money and has to
   * be rendered by the components that know the visitor's currency, not formatted into
   * a string here.
   */
  image?: string;
  detail?: ReactNode;
};

const SPACER = '---';

const useAutoComplete = (fetcher: Fetcher, callback: Callback) => {
  const [query, setQuery] = useState<string>('');
  const [open, setOpen] = useState<boolean>(false);
  const [items, setItems] = useState<Item[]>([]);
  const [selectedItemIndex, setSelectedItemIndex] = useState<number>(-1);

  const inputRef = useRef<HTMLInputElement>(null);

  const isSpacer = (index: number) => {
    return items[index]?.title === '---';
  };

  const incrementSelectedItemIndex = () => {
    let index = Math.min(items.length - 1, selectedItemIndex + 1);

    if (isSpacer(index)) {
      index = Math.min(items.length - 1, index + 1);
    }

    setSelectedItemIndex(index);
  };

  const decrementSelectedItemIndex = () => {
    let index = Math.max(-1, selectedItemIndex - 1);

    if (isSpacer(index)) {
      index = Math.max(-1, index - 1);
    }

    setSelectedItemIndex(index);
  };

  const clearQuery = () => {
    setQuery('');
  };

  const isSelected = (index: number): boolean => {
    return index === selectedItemIndex;
  };

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    setSelectedItemIndex(-1);
    setQuery(value);

    if (value !== '') {
      (async () => {
        const items = await fetcher(value);
        setOpen(true);
        setItems(items);
      })();
    } else {
      setOpen(false);
      setItems([]);
    }
  };

  const onBlur = () => {
    setOpen(false);
  };

  const onFocus = () => {
    setOpen(true);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowUp') {
      decrementSelectedItemIndex();
      event.preventDefault();
    } else if (event.key === 'ArrowDown') {
      incrementSelectedItemIndex();
      event.preventDefault();
    } else if (event.key === 'Enter') {
      if (selectedItemIndex >= 0) {
        onClick(items[selectedItemIndex]);
        event.preventDefault();
      } else if (query.trim() !== '') {
        // nothing highlighted, search for whatever was typed
        onClick({ title: query.trim() });
        event.preventDefault();
      }
    } else if (event.key === 'Escape') {
      setOpen(false);
      setSelectedItemIndex(-1);
      event.preventDefault();
    }
  };

  const onClick = (item?: Item) => {
    const selectedItem = item || { title: query };
    setQuery(selectedItem.title);
    setSelectedItemIndex(-1);
    setItems([]);
    setOpen(false);

    inputRef?.current?.blur();

    callback(selectedItem);
  };

  const inputProps = {
    ref: inputRef,
  };

  const itemsContainerProps = {
    ref: (ref: any) => {
      ref?.addEventListener('mousedown', (event: MouseEvent) => event.preventDefault());
    },
  };

  return {
    query,
    clearQuery,
    open,
    items,
    selectedItemIndex,
    incrementSelectedItemIndex,
    decrementSelectedItemIndex,
    isSelected,
    onChange,
    onBlur,
    onFocus,
    onKeyDown,
    onClick,
    inputProps,
    inputRef,
    itemsContainerProps,
    isSpacer,
  };
};

export default useAutoComplete;
export { SPACER };
